const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || ''

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (cb: () => void) => void
        execute: (siteKey: string, options: { action: string }) => Promise<string>
      }
    }
  }
}

let scriptPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (!SITE_KEY) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    if (typeof document === 'undefined') return resolve()
    if (window.grecaptcha?.enterprise) return resolve()

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-recaptcha-enterprise]',
    )
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('recaptcha load error')), {
        once: true,
      })
      return
    }

    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(SITE_KEY)}`
    script.async = true
    script.defer = true
    script.dataset.recaptchaEnterprise = 'true'
    script.onload = () => resolve()
    script.onerror = () => {
      scriptPromise = null
      reject(new Error('recaptcha load error'))
    }
    document.head.appendChild(script)
  })
  return scriptPromise
}

export function isRecaptchaConfigured(): boolean {
  return Boolean(SITE_KEY)
}

export async function executeRecaptcha(action: string): Promise<string | null> {
  if (!SITE_KEY) return null
  try {
    await loadScript()
    if (!window.grecaptcha?.enterprise) return null
    return await new Promise<string>((resolve, reject) => {
      window.grecaptcha!.enterprise.ready(() => {
        window
          .grecaptcha!.enterprise.execute(SITE_KEY, { action })
          .then(resolve)
          .catch(reject)
      })
    })
  } catch (err) {
    console.warn('reCAPTCHA execute failed', err)
    return null
  }
}
